import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Slider,
  MenuItem,
  Collapse,
} from "@material-ui/core";
import { Add, Remove, ExpandMore, ExpandLess } from "@material-ui/icons";
import _ from "lodash";

const useStyles = makeStyles(() => ({
  card: { margin: "5px" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  itemCard: { margin: "8px 0", backgroundColor: "#3a3a3a" },
  itemHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  },
  itemBody: { marginTop: "10px" },
  field: { marginBottom: "12px", width: "100%" },
}));

/**
 * Finds the lowest unused positive integer suffix for the given idPrefix among the map's keys,
 * so newly added items get ids like `enemy1_1`, `enemy1_2`, ... reusing freed-up numbers.
 *
 * @param {object} map
 * @param {string} idPrefix
 * @returns {string}
 */
function getNextItemKey(map, idPrefix) {
  const usedNumbers = new Set();
  Object.keys(map || {}).forEach((key) => {
    if (!key.startsWith(idPrefix)) return;
    const suffix = key.slice(idPrefix.length);
    const num = parseInt(suffix, 10);
    if (!Number.isNaN(num) && String(num) === suffix) {
      usedNumbers.add(num);
    }
  });
  let candidate = 1;
  while (usedNumbers.has(candidate)) {
    candidate += 1;
  }
  return `${idPrefix}${candidate}`;
}

function guardPermission(userPermissions, createSnackbar) {
  if (userPermissions && userPermissions["editContent"] === false) {
    createSnackbar("You do not have permission to update, contact your administrator.", "warning", false);
    return false;
  }
  return true;
}

/**
 * Renders a single field editor for an item, based on itemSettings[i].editorType.
 * Supported types: sliderGeneric, switchGeneric, editorText, selectorString.
 * Falls back to a plain text field for unknown types, and honors dotted paths (e.g. "position.x").
 *
 * @param props
 * @param props.classes
 * @param props.itemSetting
 * @param props.item
 * @param props.userPermissions
 * @param props.createSnackbar
 * @param props.onChange
 * @param props.onLiveChange drag-time preview; when set, sliders persist on release
 * @returns {JSX.Element|null}
 */
function ItemFieldEditor(props) {
  const classes = props.classes;
  const itemSetting = props.itemSetting;
  const { caption, path, editorType, min, max, step, items } = itemSetting;
  const rawValue = _.get(props.item, path);
  const hasLivePreview = _.isFunction(props.onLiveChange);

  function emitChange(value) {
    if (!guardPermission(props.userPermissions, props.createSnackbar)) return;
    props.onChange(path, value);
  }

  function emitLive(value) {
    if (!guardPermission(props.userPermissions, props.createSnackbar)) return;
    props.onLiveChange(path, value);
  }

  if (editorType === "sliderGeneric") {
    const numericValue = typeof rawValue === "number" ? rawValue : parseFloat(rawValue) || 0;
    return (
      <div className={classes.field}>
        <Typography variant="body2" gutterBottom>
          {caption}
        </Typography>
        <Slider
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          value={numericValue}
          min={_.isNil(min) ? 0 : min}
          max={_.isNil(max) ? 100 : max}
          step={_.isNil(step) ? 1 : step}
          valueLabelDisplay="auto"
          onChange={(event, newValue) => {
            if (hasLivePreview) {
              emitLive(newValue);
              return;
            }
            emitChange(newValue);
          }}
          onChangeCommitted={(event, newValue) => {
            if (!hasLivePreview) return;
            emitChange(newValue);
          }}
        />
      </div>
    );
  }

  if (editorType === "switchGeneric") {
    return (
      <FormControlLabel
        className={classes.field}
        label={caption}
        control={
          <Switch
            checked={!!rawValue}
            onChange={(event) => {
              emitChange(event.target.checked);
            }}
          />
        }
      />
    );
  }

  if (editorType === "selectorString") {
    const allNumeric = (items || []).every((option) => {
      const n = Number(option);
      return String(option).trim() !== "" && !Number.isNaN(n) && String(n) === String(option).trim();
    });
    return (
      <TextField
        select
        className={classes.field}
        label={caption}
        value={_.isNil(rawValue) ? "" : String(rawValue)}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        onChange={(event) => {
          const next = event.target.value;
          emitChange(allNumeric ? Number(next) : next);
        }}
      >
        {(items || []).map((option, index) => (
          <MenuItem key={index} value={String(option)}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  // editorText and any unrecognized editorType fall back to a plain text field.
  return (
    <TextField
      className={classes.field}
      label={caption}
      value={_.isNil(rawValue) ? "" : rawValue}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
      onChange={(event) => {
        emitChange(event.target.value);
      }}
    />
  );
}

/**
 * Generic editor for an object map in gameConfig (id -> item config), with Add/Remove of keyed items.
 * Removing an item drops its settings entirely; adding one clones `setting.defaultItem` under a fresh key.
 *
 * @param props
 * @param props.label
 * @param props.isVisible
 * @param props.classes shared TreeView classes (expects `classes.card`)
 * @param props.value the object map (id -> item config)
 * @param props.setValue(newMap) called with the whole updated map
 * @param props.livePreview(path, newMap) optional; iframe-only patch (no React/persist)
 * @param props.setting the schema setting: { idPrefix, defaultItem, itemSettings }
 * @param props.userPermissions
 * @param props.createSnackbar
 * @returns {JSX.Element|null}
 */
export default function editorObjectMap(props) {
  const classes = useStyles();
  const setting = props.setting || {};
  const idPrefix = setting.idPrefix || "item_";
  const defaultItem = setting.defaultItem || {};
  const itemSettings = setting.itemSettings || [];
  const mapPath = `gameConfig.components.${props.componentID}.${setting.path}`;
  const hasLivePreview = _.isFunction(props.livePreview);

  const [map, setMap] = useState(_.isPlainObject(props.value) ? props.value : {});
  const [expandedKeys, setExpandedKeys] = useState({});

  useEffect(() => {
    setMap(_.isPlainObject(props.value) ? props.value : {});
  }, [props.value]);

  function previewMap(nextMap) {
    if (!hasLivePreview) return;
    props.livePreview(mapPath, nextMap);
  }

  function addItem(event) {
    event.preventDefault();
    if (!guardPermission(props.userPermissions, props.createSnackbar)) return;
    const newKey = getNextItemKey(map, idPrefix);
    const newMap = { ...map, [newKey]: _.cloneDeep(defaultItem) };
    setMap(newMap);
    setExpandedKeys((prev) => ({ ...prev, [newKey]: true }));
    previewMap(newMap);
    // Persist after the iframe patch so cloneDeep of a large version (EPCG)
    // does not stall the add/remove click itself.
    if (hasLivePreview) {
      requestAnimationFrame(() => props.setValue(newMap));
    } else {
      props.setValue(newMap);
    }
  }

  function removeItem(key) {
    if (!guardPermission(props.userPermissions, props.createSnackbar)) return;
    const newMap = { ...map };
    delete newMap[key];
    setMap(newMap);
    previewMap(newMap);
    if (hasLivePreview) {
      requestAnimationFrame(() => props.setValue(newMap));
    } else {
      props.setValue(newMap);
    }
  }

  function withUpdatedField(key, fieldPath, value) {
    const newMap = _.cloneDeep(map);
    _.set(newMap, [key, ...String(fieldPath).split(".")], value);
    return newMap;
  }

  function previewItemField(key, fieldPath, value) {
    const newMap = withUpdatedField(key, fieldPath, value);
    setMap(newMap);
    previewMap(newMap);
  }

  function updateItemField(key, fieldPath, value) {
    const newMap = withUpdatedField(key, fieldPath, value);
    setMap(newMap);
    props.setValue(newMap);
  }

  function toggleExpanded(key) {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  if (!props.isVisible) {
    return null;
  }

  const keys = Object.keys(map);

  return (
    <Card className={_.get(props, "classes.card", classes.card)}>
      <CardContent>
        <div className={classes.header}>
          <Typography variant={"h6"}>{props.label}</Typography>
          <IconButton color="secondary" onClick={addItem}>
            <Add />
          </IconButton>
        </div>

        {keys.length === 0 && (
          <Typography variant="body2" style={{ opacity: 0.6, marginTop: "8px" }}>
            No items yet.
          </Typography>
        )}

        {keys.map((key) => {
          const item = map[key];
          const isExpanded = !!expandedKeys[key];
          return (
            <Card key={key} className={classes.itemCard}>
              <CardContent>
                <div className={classes.itemHeader} onClick={() => toggleExpanded(key)}>
                  <Typography variant="subtitle1">{key}</Typography>
                  <div>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleExpanded(key);
                      }}
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeItem(key);
                      }}
                    >
                      <Remove />
                    </IconButton>
                  </div>
                </div>
                <Collapse in={isExpanded}>
                  <div className={classes.itemBody}>
                    {itemSettings.map((itemSetting, index) => (
                      <ItemFieldEditor
                        key={`${key}.${itemSetting.path}.${index}`}
                        classes={classes}
                        itemSetting={itemSetting}
                        item={item}
                        userPermissions={props.userPermissions}
                        createSnackbar={props.createSnackbar}
                        onChange={(fieldPath, value) => updateItemField(key, fieldPath, value)}
                        onLiveChange={
                          hasLivePreview
                            ? (fieldPath, value) => previewItemField(key, fieldPath, value)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </CardContent>
    </Card>
  );
}
